import React, { useCallback, useState } from "react";
import { Typography, Divider, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import NCRFormHeading from '../NCRFormHeading';
import NCRFormLabel from '../NCRFormLabel';
import { useDropzone } from "react-dropzone";
import toast from 'react-hot-toast';
import { processFileForUpload } from "../../../utils/imageCompressor";

export default function Attachments({ content, onChange }) {
  const [isCompressing, setIsCompressing] = useState(false);

  const theme = createTheme({
    components: {
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: "14px",
            fontWeight: 400,
            color: "rgba(84, 84, 84, 1)",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
              fontWeight: 400,
              color: "rgba(84, 84, 84, 1)",
              opacity: 1,
            },
          },
        },
      },
    },
  });

  const handleFileChange = (file) => {
    if (!(file instanceof File)) {
      console.warn("Skipped non-file input:", file);
      return;
    }

    if (file) {
      onChange({
        action_attachments: file,
        action_attachments_file_name: file.name
      });
    }
  };

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      toast.error("Only PDF, JPG, JPEG, and PNG files are allowed.");
      return;
    }
    const file = acceptedFiles[0];
    if (!file) return;

    const fileSizeMB = file.size / (1024 * 1024);

    // If file is over 10MB, reject completely
    if (fileSizeMB > 10) {
      toast.error("File size should not exceed 10MB!");
      return;
    }

    // If file is between 2MB and 10MB, try to compress (only for images)
    if (fileSizeMB > 2) {
      if (!file.type.startsWith('image/')) {
        toast.error("PDF files must be under 2MB. Only images can be compressed.");
        return;
      }

      setIsCompressing(true);
      try {
        const { file: processedFile, wasCompressed } = await processFileForUpload(file, 2, 10);
        if (wasCompressed) {
          toast.success(`Image compressed from ${fileSizeMB.toFixed(1)}MB to ${(processedFile.size / (1024 * 1024)).toFixed(1)}MB`);
        }
        handleFileChange(processedFile);
      } catch (error) {
        toast.error(error.message || "Failed to compress image");
      } finally {
        setIsCompressing(false);
      }
      return;
    }

    // File is under 2MB, use as is
    handleFileChange(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    multiple: false,
  });

  const handleFileDeleteIcon = () => {
    onChange({ action_attachments: "", action_attachments_file_name: "" });
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Box>
          <Box sx={{ p: 3 }}>
            <Box>
              <NCRFormHeading tooltipData={
                [
                  { title: "Attachments", description: "Upload any supporting documents, images, or reports related to the actions taken." },
                ]
              }>
                Attachments
              </NCRFormHeading>
              {content.inputDisable ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    src="/images/pdf-icon.png"
                    alt="NC Pro"
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                  <NCRFormLabel
                    sx={{
                      px: 1,
                      fontStyle: !content.formData.action_attachments ? 'italic' : 'normal',
                      '&:hover': content.formData.action_attachments ? { cursor: 'pointer' } : {},
                    }}
                    onClick={() => {
                      if (content.formData.action_attachments) {
                        window.open(`${process.env.REACT_APP_BACKEND_APPLICATION_URL}/uploads/${content.formData.action_attachments.filename}`, '_blank');
                      }
                    }}
                  >
                    {content.formData.action_attachments?.originalname || 'File does not exist'}
                  </NCRFormLabel>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: "1px dashed rgba(0, 0, 0, 0.3)",
                      py: 1,
                      px: 4,
                      textAlign: "center",
                      cursor: isCompressing ? "wait" : "pointer",
                      borderRadius: "5px",
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      opacity: isCompressing ? 0.7 : 1,
                    }}
                    onChange={handleFileChange}
                  >
                    <Box sx={{ display: "none" }}>
                      <input {...getInputProps()} disabled={isCompressing} />
                    </Box>
                    {isCompressing ? (
                      <>
                        <CircularProgress size={24} sx={{ mb: 1 }} />
                        <Typography sx={{ fontFamily: 'Lato', fontSize: '14px', fontWeight: 400, color: 'rgba(84, 84, 84, 1)' }}>
                          Compressing image...
                        </Typography>
                      </>
                    ) : (
                      <>
                        <img
                          src="/images/upload-icon.png"
                          alt="NC Pro"
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Lato',
                            fontSize: '14px',
                            fontWeight: 400,
                          }}>
                          <span style={{ textDecoration: 'underline', color: 'rgba(47, 70, 216, 1)' }}>Browse</span> or <span style={{ color: 'rgba(84, 84, 84, 1)' }}>Drag & drop your files</span>
                        </Typography>
                      </>
                    )}
                  </Box>
                  {content.formData.action_attachments && (
                    <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
                      <img
                        src="/images/pdf-icon.png"
                        alt="NC Pro"
                        style={{ maxWidth: "100%", height: "auto" }}
                      />
                      <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 1)', p: 1, }}>
                        {content.formData.action_attachments_file_name || content.formData.action_attachments?.originalname}
                      </Typography>
                      <img
                        src="/images/file-delete-icon.png"
                        alt="NC Pro"
                        onClick={handleFileDeleteIcon}
                        style={{ maxWidth: "100%", height: "auto", cursor: 'pointer' }}
                      />
                    </Box>
                  )}
                  <NCRFormLabel sx={{ fontSize: '16px', fontWeight: 500, mt: 1 }}>Maximum file size: 10MB</NCRFormLabel>
                </Box>
              )}
            </Box>
          </Box>
          {content.label === "action" ? <div style={{ paddingBottom: '10px' }}></div> : <Divider sx={{ mt: 1.5, mb: 0.5 }} />}
        </Box>
      </ThemeProvider >
    </>
  );
};
